import { PlusIcon } from "@phosphor-icons/react";
import { useEffect } from "react";
import { useInView } from "react-intersection-observer";
import type { SessionSummary } from "../../generated/api/types.gen";
import { GalleryImageCard } from "../GalleryImageCard";
import { LoadingSpinner } from "../LoadingSpinner";

interface GalleryDesignGridProps {
  sessions: SessionSummary[];
  hasMore: boolean;
  isFetchingMore: boolean;
  onLoadMore: () => void;
  onSessionClick: (sessionId: string) => void;
  onStartNewDesign: () => void;
}

export function GalleryDesignGrid({
  sessions,
  hasMore,
  isFetchingMore,
  onLoadMore,
  onSessionClick,
  onStartNewDesign,
}: GalleryDesignGridProps) {
  const { ref: sentinelRef, inView } = useInView({ rootMargin: "200px" });

  useEffect(() => {
    if (inView && hasMore && !isFetchingMore) {
      onLoadMore();
    }
  }, [inView, hasMore, isFetchingMore, onLoadMore]);

  return (
    <div>
      <div className="mb-3 flex items-center justify-between">
        <div className="text-lg leading-6 font-semibold text-[var(--text-primary)]">
          Designs
        </div>
      </div>
      {/* Always a fixed 3 columns per the design — independent of the drawer
          and assets sidebar, which would otherwise flip the count as they
          take/give back horizontal space. */}
      <div className="grid grid-cols-3 gap-5">
        <StartNewDesignGridCard onClick={onStartNewDesign} />
        {sessions.map((session) => (
          <GalleryImageCard
            key={session.session_id}
            session={session}
            onClick={() => onSessionClick(session.session_id)}
          />
        ))}
      </div>
      {hasMore && (
        <div
          ref={sentinelRef}
          className="flex items-center justify-center py-6"
          aria-hidden={!isFetchingMore}
        >
          {isFetchingMore && <LoadingSpinner size="md" />}
        </div>
      )}
    </div>
  );
}

function StartNewDesignGridCard({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex aspect-square items-center justify-center rounded-[22px] border border-dashed border-[var(--stroke-field)]/45 text-[var(--text-primary)] shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-xl"
    >
      <span className="inline-flex items-center gap-2 rounded-lg border border-[var(--accent-secondary-stroke)] bg-[var(--Buttons-Secondary-Button---Fill)] px-4 py-2 text-sm font-semibold transition">
        Add New Design
        <PlusIcon size={14} weight="bold" aria-hidden />
      </span>
    </button>
  );
}
